import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { useCurrentUser } from "@/common/hooks/useCurrentUser";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { ClientUserData } from "@/common/types/client/user.type";
import { FormTransformers } from "@/common/utils/formTransformers";
import { CompanyBranchData } from "@/features/company/types/company.type";
import { branchHierarchyForUserAccessOptions } from "@/features/companyBranches/constants/branchHierarchyForUserAccess.constant";
import { useCreateCompanyBranchUserAccesses } from "@/features/companyBranches/hooks/useCreateCompanyBranchUserAccesses";
import { clientUserBranchAccessSchema, ClientUserBranchAccessValidationSchema } from "@/features/companyBranches/schemas/updateUserAccesses.schema";
import { ClientUserBranchAccessesTransformer } from "@/features/companyBranches/services/clientUserBranchAccessesTransformer";
import { useClientUsers } from "@/features/users/hooks/useClientUsers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

interface CompanyBranchUserAccessesCreateProps {
  companyBranch?: CompanyBranchData;
}

const CompanyBranchUserAccessesCreate = ({ companyBranch }: CompanyBranchUserAccessesCreateProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [searchUser, setSearchUser] = useDebounce('');

  const { data: currentUser } = useCurrentUser({});

  const {
    data: companyBranchUsersOptions,
  } = useClientUsers({
    filters: {
      search: searchUser,
    },
    mapper: Mapper.mapToOptions<ClientUserData>({
      valueFieldName: 'id',
      labelFieldName: 'full_name',
    }),
  });

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ClientUserBranchAccessValidationSchema>({
    resolver: zodResolver(clientUserBranchAccessSchema),
  });

  const {
    mutate: createCompanyBranchUserAccesses,
    isPending: createCompanyBranchUserAccessesIsPending,
  } = useCreateCompanyBranchUserAccesses({
    companyBranch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientUserBranchAccesses'] });
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
        setMessages,
        transformer: ClientUserBranchAccessesTransformer.errorsTransformer(),
      })(error);
    },
  });

  const onSubmit = (data: ClientUserBranchAccessValidationSchema) => {
    createCompanyBranchUserAccesses(data);
  };

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            operationKlassToCheckPermission="Client::CompanyBranches::UpdateUserAccesses"
            disabled={!companyBranch}
          >
            Adicionar

            <PlusIcon strokeWidth={1.5} />
          </Button>
        )}
        bodyClassName='max-h-[calc(90vh-72px-48px)] overflow-auto'
        title="Acesso a filial"
        titleClass="text-xl"
        useCancellableFooter
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        confirmButtonIsLoading={createCompanyBranchUserAccessesIsPending}
        disabled={createCompanyBranchUserAccessesIsPending}
        showClose={false}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
            <SelectController
              control={control}
              name="user"
              label="Usuário"
              options={companyBranchUsersOptions.filter((option) => option.value !== currentUser?.id)}
              onInputChange={setSearchUser}
              errorMessage={errors.user?.message || undefined}
              isDisabled={createCompanyBranchUserAccessesIsPending}
              markAsRequired
            />

            <SelectController
              control={control}
              name="branch_hierarchy_for_user_access"
              label="Permissão"
              options={branchHierarchyForUserAccessOptions}
              errorMessage={errors.branch_hierarchy_for_user_access?.message || undefined}
              isDisabled={createCompanyBranchUserAccessesIsPending}
              markAsRequired
            />
          </form>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  )
}

export default CompanyBranchUserAccessesCreate;
